import type { UserRole } from "./types.js";

/**
 * Every gated action in ZenForge.
 *
 * Flat strings rather than a role hierarchy: roles map onto sets of these, so
 * splitting a role is a change to the `roles` table rather than to every call
 * site that asked "is this an admin?".
 *
 * Reads are deliberately absent. Any signed-in user may look at the queue, the
 * executions list or a collection; permissions gate the actions that change
 * something, cost money, or publish.
 *
 * This union is still compile-time: permissions are what the *code* enforces,
 * so a new one only means anything alongside a route that checks it. Roles are
 * data; permissions are code.
 */
export type Permission =
  | "knowledge:upload"
  | "collection:create"
  | "collection:edit"
  | "collection:delete"
  | "generation:run"
  | "pipeline:run"
  | "content:review"
  | "content:approve"
  | "content:reject"
  | "questions:push"
  | "hierarchy:create"
  | "hierarchy:edit"
  | "hierarchy:delete"
  | "users:manage"
  | "settings:manage"
  | "audit:view";

/** Every permission, for the "all" case and for validating input. */
export const ALL_PERMISSIONS: readonly Permission[] = [
  "knowledge:upload",
  "collection:create",
  "collection:edit",
  "collection:delete",
  "generation:run",
  "pipeline:run",
  "content:review",
  "content:approve",
  "content:reject",
  "questions:push",
  "hierarchy:create",
  "hierarchy:edit",
  "hierarchy:delete",
  "users:manage",
  "settings:manage",
  "audit:view",
];

/** Human-readable labels and grouping for the permission editor. */
export const PERMISSION_GROUPS: readonly {
  group: string;
  permissions: readonly { permission: Permission; label: string; danger?: boolean }[];
}[] = [
  {
    group: "Knowledge",
    permissions: [{ permission: "knowledge:upload", label: "Upload source PDFs" }],
  },
  {
    group: "Collections",
    permissions: [
      { permission: "collection:create", label: "Create collections" },
      { permission: "collection:edit", label: "Edit collections, categories & pipelines" },
      { permission: "collection:delete", label: "Delete & archive", danger: true },
    ],
  },
  {
    group: "Generation",
    permissions: [
      { permission: "generation:run", label: "Queue category generation" },
      { permission: "pipeline:run", label: "Run pipelines" },
    ],
  },
  {
    group: "Publishing",
    permissions: [
      {
        permission: "content:review",
        // Advisory: records a verdict for the approver to act on. It never
        // publishes anything, which is why it sits apart from content:approve.
        label: "Set reviewer status on a pipeline run",
      },
      { permission: "content:approve", label: "Approve content" },
      { permission: "content:reject", label: "Reject & edit content" },
      {
        permission: "questions:push",
        label: "Publish questions to the main app",
        // Irreversible: the main app mints a fresh UUID per row and offers no
        // upsert, so a mistaken push cannot be undone by pushing again.
        danger: true,
      },
    ],
  },
  {
    group: "Syllabus",
    permissions: [
      { permission: "hierarchy:create", label: "Create chapters, sub-chapters & topics" },
      { permission: "hierarchy:edit", label: "Edit chapters, sub-chapters & topics" },
      {
        permission: "hierarchy:delete",
        label: "Delete sub-chapters & topics",
        // Hard, cascading delete on the main app with no undo — see
        // routes/hierarchy.ts.
        danger: true,
      },
    ],
  },
  {
    group: "Administration",
    permissions: [
      { permission: "users:manage", label: "Manage users & roles", danger: true },
      { permission: "settings:manage", label: "Change app settings", danger: true },
      { permission: "audit:view", label: "View the audit log" },
    ],
  },
];

/**
 * The role that cannot be edited or deleted, and always holds everything.
 *
 * Without an immutable role, a Super Admin could remove `users:manage` from
 * every role and lock the whole team out of the only screen that could undo it.
 */
export const SUPER_ADMIN: UserRole = "Super Admin";

/** The roles seeded by migration 045. Custom roles may be added beside them. */
export const BUILT_IN_ROLES = [
  "Super Admin",
  "Admin",
  "Content Generator",
  "Content Editor",
  "Content Reviewer",
  "Knowledge Uploader",
] as const;

export function isSuperAdmin(role: UserRole | null | undefined): boolean {
  return role === SUPER_ADMIN;
}

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

/**
 * Whether a set of granted permissions allows an action.
 *
 * Takes the list rather than a role name because roles are rows now — the
 * caller resolves the role to its permissions (server: from the `roles` table;
 * client: from the set the session handed back) and asks this.
 *
 * Super Admin short-circuits so that a corrupt or empty row for it can never
 * lock everyone out.
 */
export function can(
  granted: readonly string[] | null | undefined,
  permission: Permission,
  role?: UserRole | null,
): boolean {
  if (isSuperAdmin(role)) return true;
  if (!granted) return false;
  return granted.includes(permission);
}

/**
 * Whether an already-stamped permission set allows an action.
 *
 * Workers read the stamp taken at enqueue rather than the creator's current
 * role, the same way `requested_model` fixes the model at enqueue: editing a
 * role must not change what already-queued work does, or a long batch would
 * half-publish and stall.
 */
export function stampedCan(
  granted: readonly string[] | null | undefined,
  permission: Permission,
): boolean {
  if (!granted) return false;
  return granted.includes(permission);
}
